import type { ComponentProps } from 'react';
import { readableInk, statusColorVar } from '../../design/tokens';
import { cx } from './cx';

export type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-neutral text-ink-inverse',
  primary: 'bg-primary text-ink-inverse',
  success: 'bg-success-soft text-success-ink border-success-border',
  warning: 'bg-warning-soft text-warning-ink border-warning-border',
  danger: 'bg-danger-soft text-danger-ink border-danger-border',
  info: 'bg-info-soft text-info-ink border-info-border',
};

/** Ace .label / .badge: small, flat, 12px. The text carries the meaning, never the colour alone. */
export function Badge({
  tone = 'neutral',
  className,
  ...props
}: ComponentProps<'span'> & { tone?: BadgeTone }) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-ui border border-transparent px-1.5 py-px text-xs',
        TONES[tone],
        className,
      )}
      {...props}
    />
  );
}

/**
 * Issue status (legacy $g_status_colors). `color` overrides the token for admin-configured
 * statuses; the text colour is then picked for contrast automatically.
 */
export function StatusBadge({
  status,
  label,
  color,
}: {
  status: string;
  label: string;
  color?: string;
}) {
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-ui border border-black/15 px-1.5 py-px text-xs text-ink"
      style={
        color
          ? { backgroundColor: color, color: readableInk(color) }
          : { backgroundColor: statusColorVar(status) }
      }
    >
      {label}
    </span>
  );
}
